import { ReactNode } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  FunnelIcon,
} from "@heroicons/react/20/solid";
import { classNames } from "../Utils/classNames.ts";

export type SortDirection = "asc" | "desc";

export type SortLabels = { asc: string; desc: string };

function SortOption({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition",
        isActive
          ? "bg-slate-100 font-medium text-slate-900"
          : "text-slate-700 hover:bg-slate-50",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function PanelSectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </p>
  );
}

export function TableColumnHeader({
  label,
  description,
  sortDirection,
  onSort,
  sortLabels,
  isFiltered = false,
  renderFilter,
}: {
  label: string;
  description?: string;
  sortDirection?: SortDirection;
  onSort?: (direction: SortDirection) => void;
  sortLabels?: SortLabels;
  isFiltered?: boolean;
  renderFilter?: (close: () => void) => ReactNode;
}) {
  if (!onSort && !renderFilter) return <span title={description}>{label}</span>;

  return (
    <Popover>
      <PopoverButton
        title={description}
        className={classNames(
          "group inline-flex items-center gap-1 rounded-md px-1.5 py-1 -mx-1.5 text-left uppercase transition hover:bg-slate-200/60 focus:outline-none data-[open]:bg-slate-200/60",
          isFiltered ? "text-primary" : undefined,
        )}
      >
        <span>{label}</span>
        {sortDirection === "asc" && <ArrowUpIcon className="w-3.5 h-3.5" />}
        {sortDirection === "desc" && <ArrowDownIcon className="w-3.5 h-3.5" />}
        {isFiltered && <FunnelIcon className="w-3.5 h-3.5" />}
        <ChevronDownIcon className="w-3.5 h-3.5 opacity-40 transition-transform group-data-[open]:rotate-180" />
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom start"
        className="z-50 w-72 max-w-[calc(100vw-2rem)] rounded-lg bg-white p-3 text-left normal-case font-normal shadow-lg ring-1 ring-black/5 [--anchor-gap:6px] transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        {({ close }) => (
          <div className="flex flex-col gap-3">
            {description && (
              <p className="px-2 text-sm font-medium text-slate-700">
                {description}
              </p>
            )}
            {onSort && (
              <div className="flex flex-col gap-1">
                <PanelSectionTitle>Sort</PanelSectionTitle>
                <SortOption
                  label={sortLabels?.asc ?? "Ascending"}
                  icon={<ArrowUpIcon className="w-4 h-4" />}
                  isActive={sortDirection === "asc"}
                  onClick={() => {
                    onSort("asc");
                    close();
                  }}
                />
                <SortOption
                  label={sortLabels?.desc ?? "Descending"}
                  icon={<ArrowDownIcon className="w-4 h-4" />}
                  isActive={sortDirection === "desc"}
                  onClick={() => {
                    onSort("desc");
                    close();
                  }}
                />
              </div>
            )}
            {onSort && renderFilter && <hr className="border-slate-100" />}
            {renderFilter && (
              <div className="flex flex-col gap-2">
                <PanelSectionTitle>Filter</PanelSectionTitle>
                {renderFilter(close)}
              </div>
            )}
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}
